import {
    initialChatFormMessage,
    jobInterestMessagePrefill,
} from "@/services/textprefill"
import traineeService from "@/services/trainee.service"
import jobService from "@/services/job.service"
import fileService from "@/services/file.service"
import chatSteps from "@/components/chat/chatSteps"
import jobInterestSteps from "@/components/application/interest/jobInterestSteps"

/** The following data is required to be defined on the component:
 *  ref="chatForm" -- ref of the ChatForm DOM components
 *  displayedJob -- job object */
export default {
    data: () => ({
        sendMessageOnCreated: false,
        isInRequest: false,
        isSending: false,
        showChatModal: false,
        showChatForm: false,
        apprenticeshipYear: null,
        alreadyRequested: false,
        showSuccessModal: false,
        showMoreBusinessesAtSuccessModal: true,
        sendInterestOnCreated: false,
        firstname: "",
        lastname: "",
        dates: [],
    }),
    async fetch() {
        try {
            await jobService.hasJobInterest(
                this.$auth.user.trainee.id,
                this.displayedJob.id
            )
            this.alreadyRequested = true
        } catch {
            this.alreadyRequested = false
        }
        if (
            !this.alreadyRequested &&
            "anfragen" in this.$route.query &&
            this.$can.sendInterest()
        ) {
            this.isInRequest = true
            this.showChatModal = true
        }
        if (
            !this.alreadyRequested &&
            "anfrage_senden" in this.$route.query &&
            this.$can.sendInterest()
        ) {
            this.isInRequest = true
            this.isSending = true
            this.sendInterestOnCreated = true
        }
        if ("nachricht_senden" in this.$route.query && this.$can.chat()) {
            this.isInRequest = false
            this.sendMessageOnCreated = true
            this.isSending = true
            this.showChatForm = true
        }
    },
    computed: {
        isJobV3() {
            if (Number(this.displayedJob?.version) !== 3) {
                this.$sentry.captureMessage(
                    `Job ${this.displayedJob?.id} has no version 3`
                )
            }
            return true
        },
        isChatDisplayed() {
            return this.showChatModal && this.isJobV3
        },
        showFormComponent() {
            return this.isInRequest && !this.alreadyRequested
        },
        showChatComponent() {
            if (!this.showChatForm) return false
            return (
                (this.isChatDisplayed && !this.isInRequest) ||
                this.alreadyRequested
            )
        },
        initialFormMessages() {
            return initialChatFormMessage(this.$auth.user?.trainee)
        },
    },
    methods: {
        chatSteps,
        jobInterestSteps,
        async onStartRequest() {
            this.isInRequest = true
            if (this.isChatDisplayed) return
            await this.selectConversation()
            this.showChatModal = true
        },
        selectConversation() {
            return this.$store.dispatch("chat/selectConversation", {
                business: this.displayedJob.business,
            })
        },
        onChatOpen() {
            this.$store.commit("chat/setStartMessageIndex", 0)
            this.onSplit()
        },
        async onSplit() {
            this.isInRequest = false
            if (this.isChatDisplayed) return
            if (!this.$auth.loggedIn) this.showChatForm = true
            await this.selectConversation()
            this.showChatModal = true
        },
        async onInterested(dates) {
            this.apprenticeshipYear = null
            this.$store.commit("chat/setStartMessageIndex", 1)
            this.isInterested = true
            /** For apprenceship request with selected year */
            if (dates?.year) {
                this.apprenticeshipYear = dates.year
                /** Chat-Input can be initialized too slow, therefore prefill must be delayed */
            } else {
                this.dates = dates ? dates : []
            }
            setTimeout(() => this.setChatInputInterested(), 100)
            if (this.isChatDisplayed) return
            await this.onSplit()
        },
        setChatInputInterested() {
            let cpString = null
            const cp = this.displayedJob.business.public_contact_person
            if (cp) {
                cpString = `${cp.gender} ${cp.lastname}`
            }
            const prefillMessage = jobInterestMessagePrefill({
                job: this.displayedJob,
                contactPerson: cpString,
                selectedDates: this.dates.map((el) => this.toDate(el)),
                apprenticeshipYear: this.apprenticeshipYear,
            })
            this.$nextTick(() => {
                this.$root.$emit("setChatInput", prefillMessage)
            })
            this.isInterested = false
        },
        onClickOutside() {
            this.isInterested = false
            this.$root.$emit("setChatInput", "")
            this.showChatModal = false
        },
        onChatContainerInit() {
            if (this.isInterested) {
                this.setChatInputInterested()
            }
        },
        onChatFormInit(formData) {
            if (this.isInterested) {
                this.setChatInputInterested()
            }
            if (this.sendMessageOnCreated && this.$can.chat()) {
                this.chatSendRequest(formData)
                this.selectConversation()
                this.$router.replace(this.$route.path) // Remove query
            }
        },
        async chatSendRequest(formData) {
            if (!this.$auth.loggedIn || !this.$can.chat() || !formData) {
                this.handleSendRequestError(
                    {},
                    "Da ist etwas schief gelaufen. Bitte melde dich nochmal an."
                )
                return
            }
            try {
                this.isSending = true
                // send text message to conversation
                await this.$store.dispatch("chat/sendMessage", {
                    text: formData["messageText"].data ?? undefined,
                })
                const elapsedTime = Date.now() - this.startTimestamp
                this.$oabalytics.trackEvent("chatboarding_completed", {
                    elapsedTime: elapsedTime / 1000,
                })
                this.$oabalytics.trackEvent(
                    "conversion",
                    {
                        url: this.$route.path,
                        type: "message",
                    },
                    "job",
                    this.displayedJob.id
                )
                // update trainee firstname/lastname
                const traineeId = this.$auth.user.trainee.id
                await traineeService
                    .update(traineeId, {
                        firstname: formData["firstname"].data ?? undefined,
                        lastname: formData["lastname"].data ?? undefined,
                    })
                    .catch((err) => console.warn(err))
                await this.$auth.fetchUser()
                this.$refs.chatForm?.remove()
                this.showChatForm = false
                this.showMoreBusinessesAtSuccessModal = false
                this.showSuccessModal = true
                await this.$store.dispatch("chat/loadConversations")
                await this.onSplit()
            } catch (err) {
                console.error(err)
                this.handleSendRequestError(err)
            }
            this.isSending = false
        },
        handleSendRequestError(
            err,
            fallbackMessage = "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut."
        ) {
            this.isSending = false
            let message = fallbackMessage
            if (err.response?.status === 422) {
                message = err.response.data.errors.shift()
            }
            this.$store.dispatch("ui/setGlobalComponent", {
                component: import("@/components/global/ErrorMessage"),
                props: {
                    message:
                        message ?? "Du hast bereits eine Bewerbung gesendet!",
                },
            })
            this.$store.dispatch("job/setRequested", true)
            this.$emit("close")
        },
        async jobInterestSendRequest(formData) {
            if (!this.$auth.loggedIn || !formData) {
                this.handleSendRequestError(
                    {},
                    "Da ist etwas schief gelaufen. Bitte melde dich nochmal an."
                )
                return
            }
            if (!this.$can.sendInterest()) {
                this.handleSendRequestError(
                    {},
                    "Sie können keine Job-Anfrage senden"
                )
                return
            }
            try {
                this.isSending = true
                const traineeId = this.$auth.user.trainee.id

                const fileIds = await this.claimDocuments(
                    formData["documents"]?.data || []
                )

                this.$oabalytics.trackEvent("conversion", {
                    url: this.$route.path,
                    type: "jobInterest",
                })
                /* Send jobInterest */
                await jobService.createJobInterest(
                    traineeId,
                    this.displayedJob.id,
                    formData["message"].text,
                    fileIds
                )
                /* Update trainee with available data */
                await traineeService
                    .update(traineeId, {
                        class: String(formData["class"].data),
                        school_base_id: formData["school"].data.id,
                        primary_address: {
                            latitude: formData["region"].data.latitude,
                            longitude: formData["region"].data.longitude,
                            city: formData["region"].data.name,
                        },
                        highest_graduation: formData["graduation"].data,
                        firstname: formData["firstname"].data ?? undefined,
                        lastname: formData["lastname"].data ?? undefined,
                    })
                    .catch((err) => console.warn(err))
                await this.$auth.fetchUser()
                this.showSuccessModal = true
                this.showChatModal = false
                await this.$store.dispatch("job/setRequested", true)
                await this.$store.dispatch("chat/loadConversations")
                await this.selectConversation()
                await this.onSplit()
            } catch (err) {
                console.error(err)
                this.handleSendRequestError(err)
            }
            this.isSending = false
        },
        onChatAuth(formData) {
            this.firstname = formData.firstname.data
            this.lastname = formData.lastname.data
        },
        redirectUrl() {
            let route = this.$route.fullPath
            if (route.includes("?")) {
                return route + "&anfrage_senden"
            }
            return route + "?anfrage_senden"
        },
        onJobInterestAuth(formData) {
            this.firstname = formData.firstname.data
            this.lastname = formData.lastname.data
            this.showMoreBusinessesAtSuccessModal = true

            this.$auth.$storage.setCookie("redirect_action", {
                action: "send_job_interest",
                payload: this.redirectUrl(),
            })
        },
        onJobInterestFormInit(formData) {
            if (this.sendInterestOnCreated && this.$can.sendInterest()) {
                this.jobInterestSendRequest(formData)
                this.$router.replace(this.$route.path)
            }
        },
        async claimDocuments(documents) {
            let fileUploads = []
            for (let doc of documents) {
                if (doc.is_pending) {
                    fileUploads.push(
                        fileService
                            .claimFile(doc.id, doc.uuid)
                            .catch((err) => console.warn(err))
                    )
                } else {
                    fileUploads.push(doc)
                }
            }
            const files = await Promise.all(fileUploads)
            return files.map((f) => f.id)
        },
    },
    destroyed() {
        this.$store.dispatch("chat/triggerStoreSelected")
    },
}
